import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import bgImage from "../images/page-3-2.jpg";
import bgMobile from "../images/page-3-mobile.jpg"; // ⭐ NEU

const ORIGINAL_W = 6000; // Original-Bildbreite
const ORIGINAL_H = 3374; // Original-Bildhöhe

// Areas aus den coords umgerechnet
const emailRectPx = [
    3687, // x1
    2176, // y1
    4190, // x2
    2245, // y2
];
const wwwRectPx = [
    4386, // x1
    2175, // y1
    4874, // x2
    2244, // y2
];

// Rechnet die Original-Koordinaten in relative Prozente für object-fit: cover
function rectForCoverToPercent([x1, y1, x2, y2], containerW, containerH) {
    const scale = Math.max(containerW / ORIGINAL_W, containerH / ORIGINAL_H);
    const renderedW = ORIGINAL_W * scale;
    const renderedH = ORIGINAL_H * scale;
    const offsetX = (containerW - renderedW) / 2;
    const offsetY = (containerH - renderedH) / 2;

    const rx1 = x1 * scale + offsetX;
    const ry1 = y1 * scale + offsetY;
    const rw = (x2 - x1) * scale;
    const rh = (y2 - y1) * scale;

    return {
        left: `${(rx1 / containerW) * 100}%`,
        top: `${(ry1 / containerH) * 100}%`,
        width: `${(rw / containerW) * 100}%`,
        height: `${(rh / containerH) * 100}%`,
    };
}

// =======================
// ⭐ NEU: MOBILE-Konstanten
// =======================
const MOB_W = 1536; // Breite des Mobile-Originalbildes (anpassen, falls anders)
const MOB_H = 864;  // Höhe des Mobile-Originalbildes   (anpassen, falls anders)

// ⭐ NEU: Offsets für Feintuning (Mail)
const MOBILE_OFFSETS = { x: -25, y: 8, w: 0, h: 0 };

// ⭐ ADD: separate Offsets für WWW (falls nötig)
const MOBILE_OFFSETS_WWW = { x: -25, y: 8, w: 0, h: 0 }; // ⭐ ADD

// ⭐ NEU: Hilfen Mobile
const rectPxToRenderedPx = ([x1, y1, x2, y2], scale) => ({
    left: (x1 + MOBILE_OFFSETS.x) * scale,
    top: (y1 + MOBILE_OFFSETS.y) * scale,
    width: (x2 - x1) * scale,
    height: (y2 - y1) * scale,
});

// ⭐ ADD: Variante für WWW mit eigenem Offset-Set
const rectPxToRenderedPxWWW = ([x1, y1, x2, y2], scale) => ({ // ⭐ ADD
    left: (x1 + MOBILE_OFFSETS_WWW.x) * scale,
    top: (y1 + MOBILE_OFFSETS_WWW.y) * scale,
    width: (x2 - x1) * scale,
    height: (y2 - y1) * scale,
});

// ⭐ NEU: Desktop-Pixel → Prozent → Mobile-Pixel (über gleiche Prozentlage)
const toPercentRect = ([x1, y1, x2, y2], W, H) => [
    x1 / W, y1 / H, x2 / W, y2 / H
];
const fromPercentToMobPx = ([px1, py1, px2, py2]) => ([
    px1 * MOB_W, py1 * MOB_H, px2 * MOB_W, py2 * MOB_H
]);

// ⭐ NEU: Mobile-Hotspot aus Desktop-Prozenten ableiten (Startwert)
const emailRectMobPx = fromPercentToMobPx(toPercentRect(emailRectPx, ORIGINAL_W, ORIGINAL_H));
// ⭐ ADD: WWW auch für Mobile vorbereiten
const wwwRectMobPx = fromPercentToMobPx(toPercentRect(wwwRectPx, ORIGINAL_W, ORIGINAL_H)); // ⭐ ADD

function Page3() {
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768; // ⭐ NEU

    // ---------- DESKTOP ----------
    const wrapperRef = useRef(null);
    const [emailBox, setEmailBox] = useState({});
    const [wwwBox, setWwwBox] = useState({});

    useLayoutEffect(() => {
        if (isMobile) return; // ⭐ NEU: Desktop only
        const recompute = () => {
            const el = wrapperRef.current;
            if (!el) return;
            const w = el.clientWidth;
            const h = el.clientHeight;
            setEmailBox(rectForCoverToPercent(emailRectPx, w, h));
            setWwwBox(rectForCoverToPercent(wwwRectPx, w, h));
        };
        recompute();
        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(recompute);
            ro.observe(wrapperRef.current);
            return () => ro.disconnect();
        } else {
            window.addEventListener("resize", recompute);
            return () => window.removeEventListener("resize", recompute);
        }
    }, [isMobile]); // ⭐ NEU

    // ---------- MOBILE (⭐ NEU) ----------
    const scrollRef = useRef(null);
    const imgRef = useRef(null);
    const [emailBoxMob, setEmailBoxMob] = useState(null);
    const [wwwBoxMob, setWwwBoxMob] = useState(null); // ⭐ ADD

    useEffect(() => {
        if (!isMobile) return;
        const sc = scrollRef.current;
        const img = imgRef.current;
        if (!sc || !img) return;

        const compute = () => {
            const r = img.getBoundingClientRect();
            const scale = r.height / MOB_H;   // skalieren nach Höhe
            const renderedW = r.width;

            // Mail
            const b = rectPxToRenderedPx(emailRectMobPx, scale);
            setEmailBoxMob({
                position: "absolute",
                display: "block",
                left: (b.left + MOBILE_OFFSETS.x) + "px",
                top: (b.top + MOBILE_OFFSETS.y) + "px",
                width: (b.width + MOBILE_OFFSETS.w) + "px",
                height: (b.height + MOBILE_OFFSETS.h) + "px",
                // background: "#d6000059",
            });

            // ⭐ ADD: WWW
            const w = rectPxToRenderedPxWWW(wwwRectMobPx, scale);
            setWwwBoxMob({
                position: "absolute",
                display: "block",
                left: (w.left + MOBILE_OFFSETS_WWW.x) + "px",
                top: (w.top + MOBILE_OFFSETS_WWW.y) + "px",
                width: (w.width + MOBILE_OFFSETS_WWW.w) + "px",
                height: (w.height + MOBILE_OFFSETS_WWW.h) + "px",
                // background: "rgba(0,0,255,0.35)",
            });

            // ⭐ Offset in px (90px vom rechten Rand weg)
            const SCROLL_OFFSET = 90;

            // Start rechtsbündig mit Offset
            sc.scrollLeft = Math.max(0, renderedW - sc.clientWidth - SCROLL_OFFSET);
        };

        if (img.complete) compute();
        else img.addEventListener("load", compute, { once: true });

        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(compute);
            ro.observe(sc);
        } else {
            window.addEventListener("resize", compute);
        }
        return () => {
            if (img) img.removeEventListener?.("load", compute);
            if (ro) ro.disconnect();
            else window.removeEventListener("resize", compute);
        };
    }, [isMobile]);

    // ---------- RENDER ----------
    if (isMobile) {
        // ===== MOBILE VIEW (⭐ NEU) =====
        return (
            <div
                ref={scrollRef}
                style={{
                    width: "100vw",
                    height: "100vh",
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    touchAction: "pan-x",
                    position: "relative",
                }}
            >
                <img
                    ref={imgRef}
                    src={bgMobile}
                    alt=""
                    style={{ height: "100%", width: "auto", display: "block" }}
                />

                {/* Mail-Hotspot relativ zum gerenderten Bild */}
                {emailBoxMob && (
                    <a
                        href="mailto:mail@tw-a.net"
                        style={emailBoxMob}
                        title="E-Mail an mail@tw-a.net"
                        onClick={(e) => e.stopPropagation()}
                    />
                )}

                {/* ⭐ ADD: WWW-Hotspot relativ zum gerenderten Bild */}
                {wwwBoxMob && (
                    <a
                        href="https://tw-a.net"
                        style={wwwBoxMob}
                        title="www.tw-a.net besuchen"
                        onClick={(e) => e.stopPropagation()}
                        target="_blank"
                        rel="noopener noreferrer"
                    />
                )}
            </div>
        );
    }

    // ===== DESKTOP VIEW (unverändert) =====
    const stop = (e) => e.stopPropagation();
    const wrapper = {
        position: "relative",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        cursor: "pointer",
    };
    const imgStyle = {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        pointerEvents: "none",
        //transform: "scale(0.99)",
    };

    return (
        <div ref={wrapperRef} style={wrapper}>
            <img src={bgImage} alt="" style={imgStyle} />

            <a
                href="mailto:mail@tw-a.net"
                onClick={stop}
                style={{
                    position: "absolute",
                    ...emailBox,
                    color: "transparent",
                    display: "block",
                    //background: "rgba(255,0,0,0.5)", // Testoverlay
                }}
                target="_blank"
                rel="noopener noreferrer"
                title="E-Mail an mail@tw-a.net"
            >
                Mail
            </a>

            <a
                href="https://tw-a.net"
                onClick={stop}
                style={{
                    position: "absolute",
                    ...wwwBox,
                    color: "transparent",
                    display: "block",
                    //background: "rgba(0,0,255,0.5)", // Testoverlay
                }}
                target="_blank"
                rel="noopener noreferrer"
                title="www.tw-a.net besuchen"
            >
                Website
            </a>
        </div>
    );
}

export default Page3;
